// WLineIterator — step along a line at fixed world-coord increments.
// Class: game/ht.h:983-1013; Init: game/misc.cpp:697-734.
// Used by projectiles and movement; fully deterministic (isqrt + truncating division,
// all fields WCoord = signed 16-bit).

import { s16, idiv } from './int32.ts';
import { isqrtU32 } from './isqrt.ts';

export class WLineIterator {
  private m_wx = 0;
  private m_wy = 0;
  private m_wdx = 0;
  private m_wdy = 0;
  private m_cStepsRemaining = 0;

  // game/misc.cpp:697-734
  Init(wx1: number, wy1: number, wx2: number, wy2: number, nIncr: number): void {
    this.m_wx = s16(wx1);
    this.m_wy = s16(wy1);

    // WCoord wdx = wx2 - wx1 (int subtract, stored to short)
    const wdx = s16(wx2 - wx1);
    const wdy = s16(wy2 - wy1);
    // int nLen = isqrt((long)wdx*wdx + (long)wdy*wdy) — max 2*32767^2 < 2^31, exact
    const nLen = isqrtU32((Math.imul(wdx, wdx) + Math.imul(wdy, wdy)) >>> 0);

    if (nLen < nIncr) {
      this.m_cStepsRemaining = 0;
      return;
    }

    const cSteps = idiv(nLen, nIncr);
    this.m_wdx = s16(idiv(wdx, cSteps)); // int division, stored to WCoord
    this.m_wdy = s16(idiv(wdy, cSteps));
    this.m_cStepsRemaining = cSteps;
  }

  // game/ht.h:987-995
  Step(): boolean {
    if (this.m_cStepsRemaining === 0) return false;
    this.m_cStepsRemaining--;
    this.m_wx = s16(this.m_wx + this.m_wdx); // short += short
    this.m_wy = s16(this.m_wy + this.m_wdy);
    return true;
  }

  GetWX(): number {
    return this.m_wx;
  } // ht.h:997-999
  GetWY(): number {
    return this.m_wy;
  } // ht.h:1001-1003
  GetStepsRemaining(): number {
    return this.m_cStepsRemaining;
  } // ht.h:1005-1007

  // issue #98: round-trip the live iterator so an in-flight projectile resumes bit-exact
  // (all five fields are signed-16 WCoord/counter state).
  SaveState(pstm: import('../sm/stream.ts').Stream): void {
    pstm.WriteWord(this.m_wx);
    pstm.WriteWord(this.m_wy);
    pstm.WriteWord(this.m_wdx);
    pstm.WriteWord(this.m_wdy);
    pstm.WriteWord(this.m_cStepsRemaining);
  }

  LoadState(pstm: import('../sm/stream.ts').Stream): void {
    this.m_wx = s16(pstm.ReadWord());
    this.m_wy = s16(pstm.ReadWord());
    this.m_wdx = s16(pstm.ReadWord());
    this.m_wdy = s16(pstm.ReadWord());
    this.m_cStepsRemaining = pstm.ReadWord();
  }
}
